import { useEffect, useState } from 'react'
import { useEstimate } from '../context/EstimateContext'
import { Sparkles } from '../components/icons'

// Genuinely useful water-heater facts, rotated while the estimate is building.
// Keeps the wait feeling fast and reinforces the "honest advice" brand.
const FACTS = [
  'A water heater is the second-biggest energy user in most homes — roughly 18% of the power bill.',
  'Flushing the sediment out once a year can add 3–5 years to a tank’s life.',
  'Most tank heaters last 8–12 years. Past that, the odds of a sudden failure climb quickly.',
  'The “anode rod” inside the tank sacrifices itself to rust, so the steel tank doesn’t have to.',
  'Florida’s hard water builds sediment faster — which is exactly why an annual flush pays off here.',
  'Tankless units can run 20+ years and only heat water the moment you ask for it.',
  'Setting the thermostat to 120°F saves energy and prevents scalding — with plenty of hot water.',
  'A leaking tank almost never can be repaired — but a leaking valve or fitting often can.',
]

const FACT_MS = 3800

// Ambient particles drifting around the reactor (position %, size px, delay s).
const PARTICLES = [
  { left: '6%', top: '32%', size: 5, delay: 0 },
  { left: '87%', top: '22%', size: 4, delay: 0.6 },
  { left: '80%', top: '76%', size: 6, delay: 1.1 },
  { left: '13%', top: '74%', size: 4, delay: 1.7 },
  { left: '50%', top: '2%', size: 3, delay: 0.35 },
  { left: '95%', top: '52%', size: 3, delay: 1.4 },
]

export default function ExtractingStep() {
  const { busyMessage, photo } = useEstimate()
  const [factIndex, setFactIndex] = useState(0)

  useEffect(() => {
    const id = setInterval(() => setFactIndex((i) => (i + 1) % FACTS.length), FACT_MS)
    return () => clearInterval(id)
  }, [])

  return (
    <div className="flex flex-col items-center py-10 text-center animate-fade-in">
      <Reactor photoUrl={photo?.previewUrl} />

      <div className="mt-8 inline-flex items-center gap-2 text-brand-700">
        <Sparkles className="h-5 w-5" />
        <span className="font-display text-lg font-semibold">{busyMessage || 'Working on it…'}</span>
      </div>

      {/* live "processing" pulse */}
      <div className="mt-3 flex items-center gap-1.5" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className="h-1 w-5 origin-center rounded-full bg-brand-grad"
            style={{ animation: `wbwh-tick 1.4s ease-in-out ${i * 0.14}s infinite` }}
          />
        ))}
      </div>

      {/* rotating "did you know" fact card */}
      <div className="mt-7 min-h-[104px] w-full max-w-xs">
        <div key={factIndex} className="wbwh-fact glass px-5 py-4">
          <p className="eyebrow mb-1.5 flex items-center justify-center gap-1.5 text-brand-600">
            <Bolt className="h-3.5 w-3.5" />
            Did you know?
          </p>
          <p className="text-sm leading-relaxed text-subink">{FACTS[factIndex]}</p>
        </div>
      </div>

      {/* fact progress dots */}
      <div className="mt-4 flex items-center gap-1.5" aria-hidden="true">
        {FACTS.map((_, i) => (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              i === factIndex ? 'w-5 bg-brand-500' : 'w-1.5 bg-brand-200'
            }`}
          />
        ))}
      </div>

      <p className="mt-6 max-w-xs text-xs leading-relaxed text-muted">
        Building your honest repair-or-replace estimate — this usually takes just a few seconds.
      </p>
    </div>
  )
}

// The diagnostic "reactor": stacked, counter-rotating rings around a glowing
// core. When a data-plate photo exists it becomes the core and gets scanned.
function Reactor({ photoUrl }) {
  return (
    <div className="relative h-44 w-44">
      {PARTICLES.map((p, i) => (
        <span
          key={i}
          className="wbwh-float absolute rounded-full bg-aqua-400"
          style={{
            left: p.left,
            top: p.top,
            height: p.size,
            width: p.size,
            animationDelay: `${p.delay}s`,
            filter: 'blur(0.5px)',
          }}
        />
      ))}

      <svg viewBox="0 0 200 200" className="wbwh-spin-slow absolute inset-0 h-full w-full" aria-hidden="true">
        <defs>
          <linearGradient id="wbwhStroke" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#4f46e5" />
            <stop offset="55%" stopColor="#6366f1" />
            <stop offset="100%" stopColor="#06b6d4" />
          </linearGradient>
        </defs>
        <circle
          cx="100" cy="100" r="94" fill="none" stroke="url(#wbwhStroke)"
          strokeWidth="1.5" strokeDasharray="3 9" strokeLinecap="round" opacity="0.7"
        />
      </svg>

      <svg viewBox="0 0 200 200" className="wbwh-spin-mid absolute inset-0 h-full w-full" aria-hidden="true">
        <circle
          cx="100" cy="100" r="78" fill="none" stroke="url(#wbwhStroke)"
          strokeWidth="2.5" strokeLinecap="round" strokeDasharray="150 400" opacity="0.9"
        />
      </svg>

      <svg viewBox="0 0 200 200" className="wbwh-spin-fast absolute inset-0 h-full w-full" aria-hidden="true">
        <circle
          cx="100" cy="100" r="62" fill="none" stroke="#a5b4fc"
          strokeWidth="1.5" strokeDasharray="42 300" strokeLinecap="round"
        />
        <circle cx="100" cy="38" r="4.5" fill="#06b6d4" />
      </svg>

      <div className="absolute inset-0 grid place-items-center">
        <div className="wbwh-core relative grid h-[88px] w-[88px] place-items-center overflow-hidden rounded-full bg-brand-grad">
          {photoUrl && (
            <>
              <img src={photoUrl} alt="Your water heater data plate" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-brand-900/40" />
            </>
          )}
          <div className="wbwh-scan pointer-events-none absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-white/70 via-white/10 to-transparent" />
          <Drop className="relative h-9 w-9 text-white drop-shadow-[0_2px_6px_rgba(15,23,42,0.35)]" />
        </div>
      </div>
    </div>
  )
}

function Drop({ className = 'h-6 w-6' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M12 2.5c-.35 0-.67.18-.85.48C9.9 5.02 5 12.06 5 15.5a7 7 0 1 0 14 0c0-3.44-4.9-10.48-6.15-12.52A.99.99 0 0 0 12 2.5Z" />
    </svg>
  )
}

function Bolt({ className = 'h-4 w-4' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M13 2 4.5 13.5H11l-1 8.5L19.5 10H13l1-8Z" />
    </svg>
  )
}
